import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    space, lineHeights
} from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Grid, Image, Button
} from 'components/ui';
import Modal from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import SubscriptionProduct from 'components/GlobalModals/ManageSubscriptionModal/components/SubscriptionProduct';
import ManageSubscriptionBaseSections from 'components/GlobalModals/ManageSubscriptionModal/components/ManageSubscriptionBaseSections';
import ConfirmUpdateView from 'components/GlobalModals/ManageSubscriptionModal/components/ConfirmUpdateView';
import FlexIncentivesBanner from 'components/RichProfile/MyAccount/AutoReplenishment/FlexIncentivesBanner';
import { formatPrice, getSavingAmount } from 'components/RichProfile/MyAccount/AutoReplenishment/utils/price';
import { useManageSubscriptionModal } from 'components/GlobalModals/ManageSubscriptionModal/hooks/useManageSubscriptionModal';
import { useConfirmUpdateFlow } from 'components/GlobalModals/ManageSubscriptionModal/hooks/useConfirmUpdateFlow';

const AUTO_DISMISS_DELAY = 2000; // 2 seconds

/**
 * ManageSubscriptionModalRedesign - Accessible modal for managing auto-replenishment subscriptions
 *
 * Allows users to view and modify their subscription details including:
 * - Skip or pause options
 * - Delivery frequency
 * - Shipping address
 * - Payment method
 *
 * @component
 */
function ManageSubscriptionModalRedesign({
    onResume,
    currentSubscription,
    fireGenericErrorAnalytics,
    isOpen,
    isResumeSuccessful,
    onChangeDate,
    onChangeFrequency,
    onChangePayment,
    onChangeShipping,
    onDismiss,
    onPauseSubscription,
    onSkipDelivery,
    onSkuSwapClick,
    onUnsubscribeClick,
    onQtyUpdated
}) {
    const {
        creditCard, featureFlags, isLoading, localization, onModalClose, product, savingsCopy, shippingInfo, shouldShowItemVariation
    } =
        useManageSubscriptionModal({
            currentSubscription,
            onDismiss,
            onResume,
            fireGenericErrorAnalytics
        });

    const isQtyPickerEnabled = !!featureFlags.isChangeQuantityEnabled;

    const {
        view, pendingQty, committedQty, isSubmitting, submitError, confirmButtonRef, onQtyChange, onCancel, onConfirm
    } = useConfirmUpdateFlow({
        product,
        currentSubscription,
        onQtyUpdated
    });

    const displayedProduct = committedQty != null && product
        ? {
            ...product,
            qty: committedQty,
            productPrice: formatPrice({ ...product, qty: committedQty }),
            savingAmount: getSavingAmount({ ...product, qty: committedQty }, featureFlags.isFlexIncentivesEnabled)
        }
        : product;

    const baseTitle = onResume ? localization.resumeModalTitle : localization.manageSubscription;

    const handleResume = useCallback(() => {
        if (isResumeSuccessful) {
            return;
        }

        onResume?.();
    }, [onResume, isResumeSuccessful]);

    /**
     * Auto-dismiss modal after 2 seconds when resume is successful
     */
    useEffect(() => {
        if (!isResumeSuccessful) {
            return undefined;
        }

        const timeoutId = setTimeout(() => onModalClose(), AUTO_DISMISS_DELAY);

        return () => clearTimeout(timeoutId);
    }, [isResumeSuccessful, onModalClose]);

    const baseModal = (
        <Modal
            width={0}
            onDismiss={onModalClose}
            isOpen={isOpen}
            disableFocusTrap={view === 'confirm'}
        >
            <Modal.Header>
                <Modal.Title
                    aria-live='polite'
                    children={baseTitle}
                />
            </Modal.Header>
            <Modal.Body
                paddingTop={space[3]}
                paddingBottom={space[4]}
                paddingX={space[4]}
                css={styles.modalBody}
            >
                {isLoading && <Loader isShown={true} />}
                {!isLoading && product && (
                    <>
                        {featureFlags.isFlexIncentivesEnabled && !!currentSubscription.flexIncentive && (
                            <FlexIncentivesBanner
                                customCss={styles.flexIncentives}
                                flexIncentive={currentSubscription.flexIncentive}
                            />
                        )}
                        <SubscriptionProduct
                            isSkuSwapEnabled={featureFlags.isSkuSwapEnabled}
                            isQtyPickerEnabled={isQtyPickerEnabled}
                            onQtyChange={onQtyChange}
                            localization={localization}
                            onSkuSwapClick={onSkuSwapClick}
                            product={displayedProduct}
                            shouldShowItemVariation={shouldShowItemVariation}
                        />
                        <ManageSubscriptionBaseSections
                            creditCard={creditCard}
                            featureFlags={featureFlags}
                            localization={localization}
                            onChangeDate={onChangeDate}
                            onChangeFrequency={onChangeFrequency}
                            onChangePayment={onChangePayment}
                            onChangeShipping={onChangeShipping}
                            onPauseSubscription={onPauseSubscription}
                            onResume={onResume}
                            onSkipDelivery={onSkipDelivery}
                            onUnsubscribeClick={onUnsubscribeClick}
                            product={product}
                            savingsCopy={savingsCopy}
                            shippingInfo={shippingInfo}
                        />
                    </>
                )}
            </Modal.Body>
            {onResume && (
                <Modal.Footer
                    hasShadowBorderForSMUI={true}
                    paddingX={4}
                >
                    <Grid css={styles.footerActions}>
                        <Button
                            variant='secondary'
                            onClick={onModalClose}
                        >
                            {localization.cancel}
                        </Button>
                        <Button
                            variant='primary'
                            onClick={handleResume}
                        >
                            {isResumeSuccessful ? (
                                <>
                                    <Image
                                        src='/img/ufe/success-login.svg'
                                        width={20}
                                        height={20}
                                        disableLazyLoad={true}
                                        css={styles.submitButtonIcon}
                                    />
                                    {localization.resumeSuccessButtonText}
                                </>
                            ) : (
                                localization.resumeSubmitButtonText
                            )}
                        </Button>
                    </Grid>
                </Modal.Footer>
            )}
        </Modal>
    );

    return (
        <>
            {baseModal}
            {!isLoading && product && view === 'confirm' && (
                <Modal
                    width={0}
                    isDrawer={true}
                    onDismiss={onCancel}
                    isOpen={true}
                >
                    <Modal.Header>
                        <Modal.Title
                            aria-live='polite'
                            children={localization.confirmUpdate}
                        />
                    </Modal.Header>
                    <Modal.Body
                        paddingTop={space[3]}
                        paddingBottom={space[4]}
                        paddingX={space[4]}
                        css={styles.modalBody}
                    >
                        <ConfirmUpdateView
                            product={displayedProduct}
                            pendingQty={pendingQty}
                            frequency={currentSubscription.frequency}
                            frequencyType={currentSubscription.frequencyType}
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            isSubmitting={isSubmitting}
                            error={submitError}
                            confirmRef={confirmButtonRef}
                        />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
}

const styles = {
    modalBody: {
        lineHeight: lineHeights.tight,
        minHeight: '400px'
    },
    flexIncentives: {
        container: {
            marginTop: space[1],
            marginBottom: space[4]
        }
    },
    footerActions: {
        gap: space[4],
        gridTemplateColumns: '1fr 1fr'
    },
    submitButtonIcon: {
        marginRight: space[2]
    }
};

ManageSubscriptionModalRedesign.propTypes = {
    currentSubscription: PropTypes.shape({
        frequency: PropTypes.number,
        frequencyType: PropTypes.string,
        items: PropTypes.array,
        paymentId: PropTypes.string,
        profileId: PropTypes.string,
        shippingAddressId: PropTypes.string,
        flexIncentive: PropTypes.shape({
            discountPercent: PropTypes.string.isRequired,
            promoCode: PropTypes.string.isRequired,
            startDate: PropTypes.string.isRequired,
            endDate: PropTypes.string.isRequired
        })
    }).isRequired,
    fireGenericErrorAnalytics: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    isResumeSuccessful: PropTypes.bool,
    onChangeDate: PropTypes.func,
    onChangeFrequency: PropTypes.func.isRequired,
    onChangePayment: PropTypes.func.isRequired,
    onChangeShipping: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onPauseSubscription: PropTypes.func,
    onResume: PropTypes.func,
    onSkipDelivery: PropTypes.func,
    onSkuSwapClick: PropTypes.func,
    onUnsubscribeClick: PropTypes.func,
    onQtyUpdated: PropTypes.func
};

export default wrapFunctionalComponent(ManageSubscriptionModalRedesign, 'ManageSubscriptionModalRedesign');
